import { useCallback, useEffect, useRef, useState } from "react";
import { playBellPreview } from "../../utils/BellPreviewPlayer";

// Thin React wrapper around the shared playBellPreview controller. It keeps the
// same guarantees the rest of the app relies on:
//   * remote (R2) URL first, bundled /public/bells sound only as a fallback,
//   * exactly one preview audible at a time.
// `playingKey` lets any number of controls (dropdown option, row play button,
// day-preview) show a play/stop state for whatever is currently sounding. Each
// call to `play` is tagged with a caller-chosen key.
export function useBellPreview() {
    const controllerRef = useRef(null);
    const [playingKey, setPlayingKey] = useState(null);

    const stop = useCallback(() => {
        if (controllerRef.current) {
            controllerRef.current.stop();
            controllerRef.current = null;
        }
        setPlayingKey(null);
    }, []);

    const play = useCallback(
        (key, url) => {
            // Toggle: clicking the thing that is already playing stops it.
            if (playingKey === key) {
                stop();
                return;
            }
            stop();
            setPlayingKey(key);
            controllerRef.current = playBellPreview(url, {
                onEnded: () => {
                    controllerRef.current = null;
                    setPlayingKey(null);
                },
                onError: () => {
                    controllerRef.current = null;
                    setPlayingKey(null);
                },
                onStop: () => {
                    controllerRef.current = null;
                    setPlayingKey(null);
                },
            });
        },
        [playingKey, stop]
    );

    // Never leave a bell ringing when the modal unmounts.
    useEffect(() => () => stop(), [stop]);

    return { playingKey, play, stop };
}
